"use client"

import { Navigate, Outlet } from "react-router-dom"
import { AlertTriangle, X } from "lucide-react"

import { useAuth } from "../../../context/AuthContext.jsx"
import useOutbox from "../hooks/useOutbox.js"
import styles from "../css/driver.module.css"

// Matches DRIVER_ROLE_ID in server/middleware/driverAuth.js.
const DRIVER_ROLE_ID = 5

// A queued update the server rejected. Breadcrumb batches are not worth nagging
// a driver about; a completion, failure or signature that didn't land is — the
// delivery is stuck open and needs redoing.
const CRITICAL_KINDS = { complete: true, fail: true, proof: true }

const KIND_LABEL = {
  complete: "delivery completion",
  fail: "delivery failure",
  proof: "signature",
}

/**
 * Runs the offline outbox drainer for the whole driver section, and surfaces any
 * queued update the server rejected.
 *
 * The drainer has to outlive the screen that queues work: completing a delivery
 * offline navigates straight back to the dashboard, so the InTransit instance
 * that enqueued is unmounted before connectivity returns. Mounting it here, at
 * the route boundary, keeps one drainer alive across every driver screen so the
 * queue is flushed no matter where the driver ends up — and so a rejection is
 * shown wherever the driver happens to be when it comes back.
 */
function OutboxDrainer() {
  const { dropped, clearDropped } = useOutbox()

  const critical = dropped.filter((d) => CRITICAL_KINDS[d?.record?.kind])
  if (critical.length === 0) return null

  return (
    <div className={styles.outboxAlert} role="alert">
      <AlertTriangle size={18} aria-hidden="true" />
      <div className={styles.outboxAlertBody}>
        {critical.map((d, i) => (
          <div key={i}>
            A queued {KIND_LABEL[d.record.kind] || "update"} couldn&apos;t be saved
            {d.reason ? `: ${d.reason}` : "."} Please redo it from the delivery.
          </div>
        ))}
      </div>
      <button
        type="button"
        className={styles.outboxAlertClose}
        onClick={clearDropped}
        aria-label="Dismiss"
      >
        <X size={18} aria-hidden="true" />
      </button>
    </div>
  )
}

/**
 * Keeps non-drivers out of the driver screens.
 *
 * The server already enforces this on every endpoint — this only stops a
 * non-driver landing on a shell that can never load data. It is a UX guard,
 * not a security boundary.
 */
export default function DriverRouteGuard() {
  const { user, loading, isAuthenticated } = useAuth()

  // Don't redirect while the session is still resolving, or a refresh on a
  // driver URL bounces the driver out to login.
  if (loading) return null

  if (!isAuthenticated) return <Navigate to="/" replace />

  if (Number(user?.roleid) !== DRIVER_ROLE_ID) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <>
      <OutboxDrainer />
      <Outlet />
    </>
  )
}
